<template>
  <Card v-if="membership" no-shadow class="mb-4 membership-card">
    <template #title>
      <div class="membership-card__title">
        <h4 class="membership-card__heading">
          {{ membership.membership_program_name }}
        </h4>
        <img
          :src="membership.icon_url"
          alt="Membership Icon"
          class="membership-card__icon"
        />
        <span
          class="membership-card__status"
          :class="`membership-card__status--${statusVariant}`"
          :data-cy="`membership-status-${statusVariant}`"
        >
          <span class="membership-card__status-dot"></span>
          {{ statusLabel }}
        </span>
        <button
          type="button"
          class="membership-card__edit"
          aria-label="Edit membership"
          @click="$emit('edit-membership')"
        >
          <i class="fas fa-edit"></i>
        </button>
      </div>
    </template>
    <template #body>
      <div
        class="grid grid-flow-row md:grid-flow-col auto-cols-auto gap-4 divide-y *:pt-4 md:flex-row md:divide-x md:divide-y-0 *:md:pl-4 *:md:pt-0 membership-card__grid"
      >
        <div class="item-section md:-ml-4 -mt-4 md:mt-0">
          <dl class="membership-card__field-list">
            <div class="membership-card__field">
              <dt>Service address</dt>
              <dd>
                <template v-if="membership?.addresses?.length">
                  <ul class="membership-card__address-list">
                    <li
                      v-for="address in membership.addresses"
                      :key="address.id"
                    >
                      {{ fullAddress(address) }}
                    </li>
                  </ul>
                </template>
                <template v-else>--</template>
              </dd>
            </div>
            <div v-if="membership.auto_renews" class="membership-card__field">
              <dt>Next billing date</dt>
              <dd v-if="membership.renews_at">
                {{ shortDate(membership.renews_at) }}
              </dd>
              <dd v-else class="membership-card__value--warn">Unconfirmed</dd>
            </div>
            <div v-if="membership.ends_at" class="membership-card__field">
              <dt>{{ endTag }}</dt>
              <dd>{{ shortDate(membership.ends_at) }}</dd>
            </div>
            <div class="membership-card__field">
              <dt>Started</dt>
              <dd>
                {{ shortDate(membership.starts_at) }}
                <span v-if="tenureLabel" class="membership-card__field-meta"
                  >· {{ tenureLabel }}</span
                >
              </dd>
            </div>
            <div class="membership-card__field">
              <dt>Billing plan</dt>
              <dd>{{ pricingSummary }}</dd>
            </div>
            <div
              v-if="membership.covered_systems"
              class="membership-card__field"
            >
              <dt>Covered system(s)</dt>
              <dd>{{ membership.covered_systems }}</dd>
            </div>
          </dl>
          <router-link
            v-if="membership.membership_program_id"
            :to="{
              name: 'MembershipProgramEdit',
              params: { id: membership.membership_program_id }
            }"
            class="membership-card__program-link"
          >
            See full program details
            <i
              class="fas fa-arrow-right membership-card__program-link-arrow"
            ></i>
          </router-link>
        </div>
        <div class="item-section membership-card__secondary">
          <div
            v-if="healthSnapshot"
            class="membership-card__health"
          >
            <div class="membership-card__stats">
              <div class="membership-card__stat">
                <span class="membership-card__eyebrow">Renewal</span>
                <span class="membership-card__stat-value">{{
                  healthSnapshot.renewal
                }}</span>
              </div>
              <div class="membership-card__stat">
                <span class="membership-card__eyebrow">Completed services</span>
                <span class="membership-card__stat-value">{{
                  healthSnapshot.coverage
                }}</span>
              </div>
              <div class="membership-card__stat">
                <span class="membership-card__eyebrow">Expiring</span>
                <span
                  class="membership-card__stat-value"
                  :class="
                    healthSnapshot.expiringUrgent &&
                    'membership-card__stat-value--urgent'
                  "
                  >{{ healthSnapshot.expiring }}</span
                >
              </div>
            </div>
            <div v-if="recentActivity.length" class="membership-card__activity">
              <span class="membership-card__eyebrow">Recent activity</span>
              <ul class="membership-card__activity-list">
                <li
                  v-for="entry in recentActivity"
                  :key="`${entry.completed_at}-${entry.task_name}`"
                  class="membership-card__activity-item"
                >
                  <span class="membership-card__activity-date">{{
                    shortDate(entry.completed_at)
                  }}</span>
                  <span class="membership-card__activity-task">{{
                    entry.task_name
                  }}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <CoveredTasks
        :groups="membership.covered_tasks || []"
        :utilization="utilizationSummary"
        :customer="customer"
      />
    </template>
  </Card>
</template>

<script setup lang="ts">
import moment from "moment";
import { computed } from "vue";
import { RouterLink } from "vue-router";

import Card from "@/components/Card.vue";
import CoveredTasks from "@/Customer/Profile/CoveredTasks.vue";
import { dollars, fullAddress, shortDate } from "@/mixins";
import { Customer } from "@/typings/customer";
import { CustomerMembership } from "@/typings/memberships";

const props = withDefaults(
  defineProps<{
    membership: CustomerMembership | null;
    customer: Customer | null;
  }>(),
  { membership: null, customer: null }
);

defineEmits(["edit-membership"]);

const endTag = computed(() => {
  return props.membership?.renews_at ? "Cancellation Date" : "Ends On";
});

const statusVariant = computed<"active" | "unconfirmed" | "expired">(() => {
  if (!props.membership) return "expired";
  if (!props.membership.active) return "expired";
  if (props.membership.auto_renews && !props.membership.renews_at)
    return "unconfirmed";
  return "active";
});

const statusLabel = computed(() => {
  switch (statusVariant.value) {
    case "expired":
      return "Expired";
    case "unconfirmed":
      return "Unconfirmed";
    default:
      return "Active";
  }
});

const tenureLabel = computed(() => {
  if (!props.membership?.starts_at) return "";
  const start = moment(props.membership.starts_at);
  if (!start.isValid()) return "";

  const months = moment().diff(start, "months");
  if (months < 1) return "";
  if (months < 12) return `${months} mo`;

  const years = Math.floor(months / 12);
  return years === 1 ? "1 yr" : `${years} yrs`;
});

const pricingSummary = computed(() => {
  const m = props.membership;
  if (!m) return "--";
  const price = dollars(m.price_cents);
  const duration = m.duration;
  if (!duration?.period) return price;
  const period =
    duration.count > 1
      ? `${duration.count} ${duration.period}s`
      : duration.period;
  return m.auto_renews
    ? `${price} / ${period}`
    : `${price} · expires after ${period}`;
});

const allTasks = computed(
  () => props.membership?.covered_tasks?.flatMap((g) => g.tasks) ?? []
);

const utilizationSummary = computed(() => {
  if (allTasks.value.length === 0) return null;
  const completed = allTasks.value.filter(
    (t) => t.status === "Completed"
  ).length;
  return { total: allTasks.value.length, completed };
});

function compactRelative(value: string | undefined | null) {
  if (!value) return "--";
  const target = moment(value);
  if (!target.isValid()) return "--";
  const days = target.diff(moment(), "days");
  if (days < 0) return "Past";
  if (days < 31) return `${days} d`;
  const months = target.diff(moment(), "months");
  if (months < 12) return `${months} mo`;
  const years = Math.floor(months / 12);
  return years === 1 ? "1 yr" : `${years} yr`;
}

const healthSnapshot = computed(() => {
  const m = props.membership;
  if (!m || allTasks.value.length === 0) return null;

  let renewal: string;
  if (!m.active) {
    renewal = "Expired";
  } else if (m.auto_renews && m.renews_at) {
    renewal = compactRelative(m.renews_at);
  } else if (m.ends_at) {
    renewal = compactRelative(m.ends_at);
  } else {
    renewal = "--";
  }

  const coverage = utilizationSummary.value
    ? `${utilizationSummary.value.completed}/${utilizationSummary.value.total}`
    : "--";

  // "Expiring" tracks the MEMBERSHIP term end, NOT the per-task year window.
  // Task slots reset at every annual rollover on auto-renewing plans, so
  // surfacing per-task days_remaining produced a misleading "0d" near the
  // boundary. No ends_at = auto-renews indefinitely = "--".
  let expiring = "--";
  let expiringUrgent = false;
  if (m.ends_at) {
    expiring = compactRelative(m.ends_at);
    const days = moment(m.ends_at).diff(moment(), "days");
    expiringUrgent = days >= 0 && days < 30;
  }

  return { renewal, coverage, expiring, expiringUrgent };
});

const recentActivity = computed(() =>
  allTasks.value
    .filter((t) => t.completed_at)
    .sort((a, b) =>
      (b.completed_at as string).localeCompare(a.completed_at as string)
    )
    .slice(0, 2)
);
</script>

<style lang="scss" scoped>
.flex-grow-2 {
  flex-grow: 2;
}

.membership-card__title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
}
.membership-card__heading {
  margin: 0;
  font-size: 1.0625rem;
  font-weight: 600;
  letter-spacing: -0.005em;
  color: #0f172a;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}
.membership-card__icon {
  height: 1.125rem;
  width: auto;
  flex-shrink: 0;
}
.membership-card__status {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.125rem 0.5rem 0.125rem 0.4375rem;
  border-radius: 9999px;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  line-height: 1.4;
  white-space: nowrap;
  margin-left: 0.125rem;
}
.membership-card__status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  flex-shrink: 0;
}
.membership-card__status--active {
  background: #ecfdf5;
  color: #047857;
}
.membership-card__status--unconfirmed {
  background: #fffbeb;
  color: #b45309;
}
.membership-card__status--expired {
  background: #f1f5f9;
  color: #64748b;
}

.membership-card__edit {
  appearance: none;
  background: transparent;
  border: 0;
  padding: 0.25rem;
  margin-left: 0.125rem;
  color: var(--blue);
  cursor: pointer;
  border-radius: 4px;
  line-height: 1;
  font-size: 0.8125rem;
  transition: background 120ms ease, color 120ms ease;

  &:hover {
    background: #eff6ff;
  }
  &:focus-visible {
    outline: 2px solid var(--blue);
    outline-offset: 1px;
  }
}

.membership-card__field-list {
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}
.membership-card__field {
  display: grid;
  grid-template-columns: 9.5rem minmax(0, 1fr);
  align-items: baseline;
  gap: 0.75rem;

  dt {
    font-size: 0.6875rem;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #64748b;
    margin: 0;
    padding-top: 0.0625rem;
  }
  dd {
    margin: 0;
    font-size: 0.875rem;
    color: #0f172a;
  }

  @media (max-width: 640px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 0.125rem;
  }
}
.membership-card__value--warn {
  font-style: italic;
  color: #dc2626;
}
.membership-card__field-meta {
  color: var(--gray);
  font-weight: 400;
  margin-left: 0.125rem;
}
.membership-card__address-list {
  list-style: none;
  padding-left: 0;
  margin: 0;

  li + li {
    margin-top: 0.125rem;
  }
}

.membership-card__eyebrow {
  display: block;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #64748b;
  margin-bottom: 0.375rem;
}
.membership-card__secondary {
  display: flex;
  flex-direction: column;
  gap: 1.125rem;
}

.membership-card__program-link {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  margin-top: 0.875rem;
  padding: 0.125rem 0;
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--blue);
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
  &:hover .membership-card__program-link-arrow {
    transform: translateX(2px);
  }
}
.membership-card__program-link-arrow {
  font-size: 0.6875rem;
  transition: transform 120ms ease;
}

.membership-card__health {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
// auto-fit + minmax stops "Completed services" wrapping mid-word ("COMPLET
// ED") when the right column gets squeezed.
.membership-card__stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(7rem, 1fr));
  gap: 0.75rem 1.25rem;
}
.membership-card__stat {
  display: flex;
  flex-direction: column;
  gap: 0.1875rem;
  min-width: 0;

  .membership-card__eyebrow {
    margin-bottom: 0;
    overflow-wrap: normal;
    word-break: normal;
    hyphens: none;
  }
}
.membership-card__stat-value {
  font-size: 0.9375rem;
  font-weight: 600;
  color: #0f172a;
  letter-spacing: -0.005em;
  white-space: nowrap;
}
.membership-card__stat-value--urgent {
  color: #b91c1c;
}

.membership-card__activity {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.membership-card__activity-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}
.membership-card__activity-item {
  display: grid;
  grid-template-columns: 5.5rem minmax(0, 1fr);
  gap: 0.5rem;
  font-size: 0.8125rem;
  align-items: baseline;
}
.membership-card__activity-date {
  color: var(--gray);
  font-variant-numeric: tabular-nums;
}
.membership-card__activity-task {
  color: #0f172a;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}
</style>
